import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import '@/styles/viewModeOverrides.css'; // forces md:* responsive classes when user picks Mobile/Desktop view
import '@/styles/sa-rmd.css'; // Super Admin — RMD design system (consolidation 2026-09)
import '@/utils/axiosAuth'; // global axios Bearer-token + 401 interceptor
import { initSessionTimeout, stopSessionTimeout } from '@/utils/sessionTimeout';
import { toast } from 'sonner';

import { ViewModeProvider } from '@/contexts/ViewModeContext';
import AriCommandCenterDrawer from '@/components/AriCommandCenterDrawer';
import { NC_LAUNCH_LOCKED, TOOLS_LAUNCH_LOCKED } from '@/config/launchFlags';
import MandatoryCommPopup from '@/components/MandatoryCommPopup';
import ScrollToTop from '@/components/ScrollToTop';
import FeedbackWidget from '@/components/FeedbackWidget';
const NewDashboard = React.lazy(() => import('@/pages/RemodDashboard'));
const PaymentLinkPage = React.lazy(() => import('@/pages/PaymentLinkPage'));
const BusinessProfileSetup = React.lazy(() => import('@/pages/BusinessProfileSetup'));
const WorkerOnboardingChecklist = React.lazy(() => import('@/pages/WorkerOnboardingChecklist'));
const NCOnboarding = React.lazy(() => import('@/pages/NCOnboarding'));
const NCPortal = React.lazy(() => import('@/pages/NCPortal'));
const SuperAdminNCManagement = React.lazy(() => import('@/pages/SuperAdminNCManagement'));
const MyDocumentsWorker = React.lazy(() => import('@/pages/MyDocumentsWorker'));
const BusinessDocumentsHub = React.lazy(() => import('@/pages/BusinessDocumentsHub'));
const SuperAdminFeedback = React.lazy(() => import('@/pages/SuperAdminFeedback'));
const DashboardRouter = React.lazy(() => import('@/pages/DashboardRouter'));
const DispatchRouter = React.lazy(() => import('@/pages/dispatch/DispatchRouter'));
const WorkHub = React.lazy(() => import('@/pages/WorkHub'));
const MoneyHub = React.lazy(() => import('@/pages/MoneyHub'));
const FeaturesList = React.lazy(() => import('@/pages/FeaturesList'));
// ToolsHub DELETED — its 3 contents (Inventory, Warranty Helper, User Management) are now individual modules on the Provider home screen
// CustomersList, CustomerProfile, CustomersHub DELETED — customer workflow is now inside Dispatch
const MaintenancePlans = React.lazy(() => import('@/pages/MaintenancePlans'));
const CreateInvoice = React.lazy(() => import('@/pages/CreateInvoice'));
const CreateEstimate = React.lazy(() => import('@/pages/CreateEstimate'));
const InvoiceView = React.lazy(() => import('@/pages/InvoiceView'));
const WarrantyPortals = React.lazy(() => import('@/pages/WarrantyPortals'));
const WarrantyPortalHelper = React.lazy(() => import('@/pages/WarrantyPortalHelper'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
import AnalyticsLogin from '@/pages/AnalyticsLogin';
const FinancialTrackerPage = React.lazy(() => import('@/pages/FinancialTrackerPage'));
const AdminFinancialTracker = React.lazy(() => import('@/pages/AdminFinancialTracker'));
const AdminVerification = React.lazy(() => import('@/pages/AdminVerification'));
const RemodWorksWallet = React.lazy(() => import('@/pages/RemodWorksWallet'));
const GoalsManager = React.lazy(() => import('@/pages/GoalsManager'));
const Membership = React.lazy(() => import('@/pages/Membership'));
const MembershipSuccess = React.lazy(() => import('@/pages/MembershipSuccess'));
const UserManagement = React.lazy(() => import('@/pages/UserManagement'));
const ContractorManagement = React.lazy(() => import('@/pages/ContractorManagement'));
// ScheduleNew removed — replaced by DispatchWorkspace
const EmailJobsV2 = React.lazy(() => import('@/pages/EmailJobsV2'));
const Invoices = React.lazy(() => import('@/pages/Invoices'));
// CreateJobFormFinal & ScheduleAppointmentForm removed — replaced by DispatchWorkspace IntakeSheet
import JobDetailView from '@/components/scheduling/JobDetailView';
const MyJobs = React.lazy(() => import('@/pages/MyJobs'));
const Refunds = React.lazy(() => import('@/pages/Refunds'));
const PublicRemodify = React.lazy(() => import('@/pages/PublicRemodify'));
const PublicEstimateView = React.lazy(() => import('@/pages/PublicEstimateView'));
const PublicPortalCreate = React.lazy(() => import('@/pages/PublicPortalCreate'));
const RemodifyPortal = React.lazy(() => import('@/pages/RemodifyPortal'));
const RemodForms = React.lazy(() => import('@/pages/RemodForms'));
const RemodPay = React.lazy(() => import('@/pages/RemodPay'));
const PublicSignForm = React.lazy(() => import('@/pages/PublicSignForm'));
const Tools = React.lazy(() => import('@/pages/Tools'));
const ToolsSignup = React.lazy(() => import('@/pages/ToolsSignup'));
const QuickReview = React.lazy(() => import('@/pages/QuickReview'));
const Messages = React.lazy(() => import('@/pages/Messages'));
const EmailSettings = React.lazy(() => import('@/pages/EmailSettings'));
const ImportData = React.lazy(() => import('@/pages/ImportData'));
const SignUp = React.lazy(() => import('@/pages/SignUp'));
const NCSignUp = React.lazy(() => import('@/pages/NCSignUp'));
const NCLandingPage = React.lazy(() => import('@/pages/NCLandingPage'));
const NCLogin = React.lazy(() => import('@/pages/NCLogin'));
const IntegrationsLeadSources = React.lazy(() => import('@/pages/IntegrationsLeadSources'));
const VerifyEmail = React.lazy(() => import('@/pages/VerifyEmail'));
const ForgotPassword = React.lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const Settings = React.lazy(() => import('@/pages/SettingsNew'));
const SettingsBranding = React.lazy(() => import('@/pages/settings/SettingsBranding'));
const SettingsProfile = React.lazy(() => import('@/pages/settings/SettingsProfile'));
const SettingsVerification = React.lazy(() => import('@/pages/settings/SettingsVerification'));
const SettingsDualRole = React.lazy(() => import('@/pages/settings/SettingsDualRole'));
const SettingsRegions = React.lazy(() => import('@/pages/settings/SettingsRegions'));
const SettingsLifecycle = React.lazy(() => import('@/pages/settings/SettingsLifecycle'));
const SettingsWorkflows = React.lazy(() => import('@/pages/settings/SettingsWorkflows'));
const SettingsTaxDocuments = React.lazy(() => import('@/pages/settings/SettingsTaxDocuments'));
const SettingsDataRetention = React.lazy(() => import('@/pages/settings/SettingsDataRetention'));
const SettingsFeatures = React.lazy(() => import('@/pages/settings/SettingsFeatures'));
const SettingsLateFees = React.lazy(() => import('@/pages/settings/SettingsLateFees'));
const SettingsPayments = React.lazy(() => import('@/pages/settings/SettingsPayments'));
const SettingsTutorials = React.lazy(() => import('@/pages/settings/SettingsTutorials'));
const SettingsSecurity = React.lazy(() => import('@/pages/settings/SettingsSecurity'));
const CompanyBranding = React.lazy(() => import('@/pages/CompanyBranding'));
const EmployeeManagement = React.lazy(() => import('@/pages/EmployeeManagement'));
const ComplianceTracking = React.lazy(() => import('@/pages/ComplianceTracking'));
// EnhancedReports removed — Reports merged into Analytics
// Pricing removed — replaced by SetupYourBusiness (/build-experience)
const SmartInventoryUnified = React.lazy(() => import('@/pages/SmartInventoryUnified'));
const ReviewsDashboard = React.lazy(() => import('@/pages/ReviewsDashboard'));
const PricingAdvisor = React.lazy(() => import('@/pages/PricingAdvisor'));
const BusinessBlueprint = React.lazy(() => import('@/pages/BusinessBlueprint'));
const TimeTracking = React.lazy(() => import('@/pages/TimeTracking'));
const BankConnect = React.lazy(() => import('@/pages/BankConnect'));
const PayrollDashboard = React.lazy(() => import('@/pages/PayrollDashboard'));
const PayConfigPage = React.lazy(() => import('@/pages/PayConfigPage'));
const ContractorFinancialTracker = React.lazy(() => import('@/pages/ContractorFinancialTracker'));
const ContractorPortal = React.lazy(() => import('@/pages/ContractorPortal'));
const EmployeePayPortal = React.lazy(() => import('@/pages/EmployeePayPortal'));
const ContractorSchedule = React.lazy(() => import('@/pages/ContractorSchedule'));
const ContractorCustomers = React.lazy(() => import('@/pages/ContractorCustomers'));
const ContractorMessages = React.lazy(() => import('@/pages/ContractorMessages'));
const SchedulerTechnicianPortal = React.lazy(() => import('@/pages/SchedulerTechnicianPortal'));
// import SchedulerDashboard from '@/pages/SchedulerDashboard'; // legacy — /scheduler-portal now redirects to /dispatcher-portal
const TechnicianSchedule = React.lazy(() => import('@/pages/TechnicianSchedule'));
const TechnicianJobs = React.lazy(() => import('@/pages/TechnicianJobs'));
const TechnicianPay = React.lazy(() => import('@/pages/TechnicianPay'));
const WorkerDocuments = React.lazy(() => import('@/pages/WorkerDocuments'));
const DispatcherPortal = React.lazy(() => import('@/pages/DispatcherPortal'));
const DispatchMapDashboard = React.lazy(() => import('@/pages/DispatchMapDashboard'));
const CustomerTracking = React.lazy(() => import('@/pages/CustomerTracking'));
const PublicCompanyProfile = React.lazy(() => import('@/pages/PublicCompanyProfile'));
const JobGallery = React.lazy(() => import('@/pages/JobGallery'));
const CompanyPortfolio = React.lazy(() => import('@/pages/CompanyPortfolio'));
const VoiceCalling = React.lazy(() => import('@/pages/VoiceCalling'));
const APIManagement = React.lazy(() => import('@/pages/APIManagement'));
// Virtual cards removed
const PaymentSuccess = React.lazy(() => import('@/pages/PaymentSuccess'));
const PublicProfile = React.lazy(() => import('@/pages/PublicProfile'));
const FindProviders = React.lazy(() => import('@/pages/FindProviders'));
const FindProvidersIndex = React.lazy(() => import('@/pages/FindProvidersIndex'));
const FoundersChecklist = React.lazy(() => import('@/pages/FoundersChecklist'));
const MyAccount = React.lazy(() => import('@/pages/MyAccount'));
const BusinessPhonePage = React.lazy(() => import('@/pages/BusinessPhonePage'));
const SoftphonePage = React.lazy(() => import('@/pages/SoftphonePage'));
// AI Center removed — Ari is global only (single panel)
const TeamHub = React.lazy(() => import('@/pages/TeamHub'));
// CustomersHub removed — customer workflow lives inside Dispatch
import { Toaster } from '@/components/ui/sonner';
import FloatingSoftphone from '@/components/FloatingSoftphone';
import { TutorialProvider } from '@/contexts/TutorialContext';
import { ActivationProvider } from '@/contexts/ActivationContext';
import { BusinessSetupProvider } from '@/contexts/BusinessSetupContext';
import { TerminologyProvider } from '@/contexts/TerminologyContext';
import ActivationGate from '@/components/ActivationGate';
import { MobileGuidanceProvider } from '@/contexts/MobileGuidanceContext';
import { TradeProvider } from '@/context/TradeContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import ProviderSurfaceGate from '@/components/ProviderSurfaceGate';
import DemoMode from '@/components/DemoMode';
import CustomerDemoMode from '@/components/CustomerDemoMode';
import ReplayDemo from '@/components/ReplayDemo';
import GlobalPortalShellToggle from '@/components/GlobalPortalShellToggle';
import GatewayHomeButton from '@/components/GatewayHomeButton';
import SuperAdminBottomNav from '@/components/SuperAdminBottomNav';
import GPSMileageTracker from '@/components/GPSMileageTracker';
import FeatureGuard from '@/components/FeatureGuard';
import RewardPopup from '@/components/RewardPopup';
import TrialExpirationGuard from '@/components/TrialExpirationGuard';

// New Features Pages (Unique ones - not merged)
const EmployeeGamification = React.lazy(() => import('@/pages/EmployeeGamification'));
const RegionalIntelligence = React.lazy(() => import('@/pages/RegionalIntelligence'));
const ServiceAreaConfig = React.lazy(() => import('@/pages/ServiceAreaConfig'));
const SupportChat = React.lazy(() => import('@/pages/SupportChat'));
const SuperAdminLogin = React.lazy(() => import('@/pages/SuperAdminLogin'));
const SuperAdminDashboard = React.lazy(() => import('@/pages/SuperAdminDashboard'));
const PlatformRulesAdmin = React.lazy(() => import('@/pages/PlatformRulesAdmin'));
const AriDiagnosticsDashboard = React.lazy(() => import('@/pages/AriDiagnosticsDashboard'));
const AriDecisionJournal = React.lazy(() => import('@/pages/AriDecisionJournal'));
const SuperAdminControls = React.lazy(() => import('@/pages/SuperAdminControls'));
const SuperAdminOperationalSimulator = React.lazy(() => import('@/pages/SuperAdminOperationalSimulator'));
const SuperAdminOperationalIntelligence = React.lazy(() => import('@/pages/SuperAdminOperationalIntelligence'));
const SuperAdminManualLeadRouting = React.lazy(() => import('@/pages/SuperAdminManualLeadRouting'));
const SuperAdminWorkspaceReviews = React.lazy(() => import('@/pages/SuperAdminWorkspaceReviews'));
const SuperAdminActivationFunnel = React.lazy(() => import('@/pages/SuperAdminActivationFunnel'));
const SuperAdminFounderProgram = React.lazy(() => import('@/pages/SuperAdminFounderProgram'));
const SuperAdminGrowth = React.lazy(() => import('@/pages/SuperAdminGrowth'));
const SecurityCenter = React.lazy(() => import('@/pages/SecurityCenter'));
const SuperAdminPhone = React.lazy(() => import('@/pages/SuperAdminPhone'));
const SuperAdminCustomerAcquisition = React.lazy(() => import('@/pages/SuperAdminCustomerAcquisition'));
const Join = React.lazy(() => import('@/pages/Join'));
const SuperAdminSearchIntelligence = React.lazy(() => import('@/pages/SuperAdminSearchIntelligence'));
const ProviderDiscoverability = React.lazy(() => import('@/pages/ProviderDiscoverability'));
import LandingPage from '@/pages/LandingPage';
import Gateway from '@/pages/Gateway';
const SetupYourBusiness = React.lazy(() => import('@/pages/SetupYourBusiness'));
const IndustryStarterOnboarding = React.lazy(() => import('@/pages/IndustryStarterOnboarding'));
const TermsAndPolicy = React.lazy(() => import('@/pages/TermsAndPolicy'));
const PayoutSetupStandalone = React.lazy(() => import('@/pages/PayoutSetupStandalone'));
const Privacy = React.lazy(() => import('@/pages/Privacy'));
const Terms = React.lazy(() => import('@/pages/Terms'));
const About = React.lazy(() => import('@/pages/About'));
const Services = React.lazy(() => import('@/pages/Services'));
const AutopilotSettings = React.lazy(() => import('@/pages/AutopilotSettings'));
const CrossModuleAnalytics = React.lazy(() => import('@/pages/CrossModuleAnalytics'));
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import PublicInstallBadge from '@/components/PublicInstallBadge';

// Note: AdvancedFinancialIntelligence, ForecastingScenarios, AdvancedTaxTools merged into AdminFinancialTracker
// Note: AdvancedQuoting merged into Estimates
// Note: VendorIntelligence merged into PartsOrders
// Note: CustomerCommunicationHub merged into CustomerProfile
// Note: PlatformExperience merged into Settings

// NOTE: CustomerRedirect (/customer/:customerId → /jobs) and SoloOrFeatureGuard
// previously inlined here are now standalone components in
//   @/pages/CustomerRedirect and @/components/SoloOrFeatureGuard
// because SharedProviderRoutes.js imports them. Locked 2026-05-30 —
// Provider Dual-Surface Architecture refactor.

// Wrapper component to access router location
import AppLockScreen from '@/components/AppLockScreen';
import useDynamicManifest from '@/hooks/useDynamicManifest';

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAI, setShowAI] = React.useState(false);
  const [appLocked, setAppLocked] = React.useState(false);
  const lastActivityRef = React.useRef(Date.now());

  // ViewMode is now owned end-to-end by ProviderSurfaceGate (dual-surface
  // architecture, locked 2026-05-30). App.js no longer renders any
  // viewport-dependent chrome itself — each surface (Mobile / Desktop)
  // owns its own footer, viewport meta, and onboarding state.

  // Per-tool PWA manifest swap — each standalone Tool installs under its
  // own name (RemodForms / RemodPay / RemodInventory) when the user is on
  // that tool's route. Falls back to the platform manifest elsewhere.
  useDynamicManifest();

  // Super Admin — light presidential theme (owner directive 2026-08).
  // Toggled via a body class so every /super-admin surface re-themes at once.
  React.useEffect(() => {
    const isSA = location.pathname.startsWith('/super-admin');
    document.body.classList.toggle('sa-rmd', isSA);
    return () => document.body.classList.remove('sa-rmd');
  }, [location.pathname]);

  // ── ARI ROLE SCOPING ──
  // business_owner / admin / owner / super_admin → full Ari (Insights + Chat + Control Center)
  // technician / contractor                      → chat-only version of the same panel
  // customer                                      → hidden entirely (customer portal is a public route, already excluded)
  const ariAccess = React.useMemo(() => {
    let role = 'business_owner';
    try {
      const rolesStr = localStorage.getItem('user_roles');
      if (rolesStr) {
        const roles = JSON.parse(rolesStr);
        if (roles.includes('customer')) role = 'customer';
        else if (roles.includes('technician')) role = 'technician';
        else if (roles.includes('contractor')) role = 'contractor';
        else if (roles.includes('tools_user')) role = 'tools_user';
        else if (roles.some(r => ['admin','owner','super_admin','business_owner'].includes(r))) role = 'business_owner';
      } else {
        const single = localStorage.getItem('user_role');
        if (single === 'customer') role = 'customer';
        else if (single === 'technician') role = 'technician';
        else if (single === 'contractor') role = 'contractor';
      }
    } catch { /* default business_owner */ }
    // Per ARI_ACCESSIBILITY_DOCTRINE.md (Feb 2026): Ari is the persistent
    // intelligence layer — universally accessible across ALL in-app portals
    // (Provider, Dispatcher, Technician, Contractor, Customer, Tools).
    // Modes are CONTEXTUAL (what to surface), NOT restrictive — every role
    // gets full conversational access; the LLM system prompt narrows
    // priority context per portal.
    if (role === 'business_owner') return { show: true, mode: 'full' };
    // Per owner directive May 16, 2026 LATE — Ari Center must include
    // BOTH Chat and Insights in every portal. Control Center stays
    // business-only (workers can't change business-wide autopilot).
    if (role === 'technician' || role === 'contractor') return { show: true, mode: 'insights_only' };
    if (role === 'customer') return { show: true, mode: 'insights_only' };
    if (role === 'tools_user') return { show: true, mode: 'insights_only' };
    return { show: true, mode: 'insights_only' };
  }, [location.pathname]);

  // App PIN lock logic
  React.useEffect(() => {
    const checkLock = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      
      try {
        // Raw fetch() bypasses the axios Bearer interceptor, so the
        // Authorization header must be attached explicitly. Otherwise
        // `/api/security/settings/{userId}` returns 401, and while the
        // .catch below swallows the error the noise shows up in console
        // on every logged-in page mount.
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/settings/${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data?.app_pin_enabled) {
          const lockAfter = data.lock_after_minutes ?? 5;
          if (lockAfter === -1) return; // Never lock (trusted device)
          
          const lastUnlock = parseInt(sessionStorage.getItem('last_unlock') || '0');
          const elapsed = (Date.now() - lastUnlock) / 60000;
          if (lockAfter === 0 || elapsed > lockAfter) {
            setAppLocked(true);
          }
        }
      } catch (e) { /* ignore */ }
    };

    checkLock();

    // Check lock on visibility change (user switches back to app)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkLock();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const handleAppUnlock = React.useCallback(() => {
    setAppLocked(false);
    sessionStorage.setItem('last_unlock', String(Date.now()));
  }, []);

  // Session lifecycle (founder ruling 2026-09-27): server-validated restore,
  // inactivity expiry, immediate clear on logout — no permanent auto-login.
  // initSessionTimeout self-gates (requires a real token) and re-runs on
  // route change so a fresh SPA login arms the timers without a reload.
  React.useEffect(() => {
      initSessionTimeout({
        onWarning: () => {
          // Create a custom centered modal-style warning instead of toast
          const warningDiv = document.createElement('div');
          warningDiv.id = 'session-timeout-warning';
          warningDiv.className = 'fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50';
          warningDiv.innerHTML = `
            <div class="bg-white rounded-xl shadow-2xl p-6 max-w-md w-[90vw] mx-4 border-4 border-red-500">
              <div class="flex items-center gap-3 mb-4">
                <div class="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <h3 class="text-xl font-bold text-gray-900">Session Expiring Soon</h3>
                  <p class="text-sm text-gray-600 mt-1">You will be signed out in 2 minutes due to inactivity</p>
                </div>
              </div>
              <button id="stay-active-btn" class="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors text-lg shadow-lg">
                Stay Active
              </button>
            </div>
          `;
          
          document.body.appendChild(warningDiv);
          
          // Add click handler for Stay Active button
          document.getElementById('stay-active-btn').addEventListener('click', () => {
            const warning = document.getElementById('session-timeout-warning');
            if (warning) {
              warning.remove();
            }
          });
          
          // Auto-remove after 2 minutes (when logout happens)
          setTimeout(() => {
            const warning = document.getElementById('session-timeout-warning');
            if (warning) {
              warning.remove();
            }
          }, 2 * 60 * 1000);
        },
        onLogout: () => {
          // Remove warning if still visible
          const warning = document.getElementById('session-timeout-warning');
          if (warning) {
            warning.remove();
          }
          toast.error('Session expired. Please log in again.');
          navigate('/login');
        }
      });

    // Cleanup on unmount
    return () => {
      stopSessionTimeout();
    };
  }, [navigate, location.pathname]);

  // Check if AI Assistant should be shown based on route
  React.useEffect(() => {
    // Don't show on auth pages, onboarding, or Super Admin pages
    const authPages = ['/login', '/signup', '/', '/forgot-password', '/reset-password', '/verify-email', '/analytics-login', '/build-experience', '/membership', '/membership/success', '/welcome-setup', '/founders-checklist', '/terms-and-policy'];
    const superAdminPages = ['/super-admin/login', '/super-admin/dashboard', '/super-admin/platform-rules', '/super-admin/ari-diagnostics', '/super-admin/ari-decisions', '/super-admin/controls', '/super-admin/manual-lead-routing', '/super-admin/operational-simulator', '/super-admin/operational-intelligence'];
    const currentPath = location.pathname;
    
    // Hide AI on Super Admin pages for security
    if (superAdminPages.some(page => currentPath.startsWith(page))) {
      setShowAI(false);
      return;
    }

    // Hide AI on Remodify portal (customer portal has its own limited Ari)
    if (currentPath.startsWith('/remodify/') || currentPath.startsWith('/customer-portal/')) {
      setShowAI(false);
      return;
    }
    
    if (authPages.includes(currentPath)) {
      setShowAI(false);
      return;
    }

    // Check if user needs to complete onboarding (hard gate)
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    
    if (!token) {
      setShowAI(false);
      return;
    }
    
    // Always source-of-truth from backend on cold load + every route
    // change. The localStorage flags (terms_accepted /
    // hasCompletedOnboarding / signup_in_progress / first_login_pending)
    // are caches only — they get scrubbed on logout and re-derived from
    // the session-status response. Locked May 17, 2026 (Provider Signup
    // Completion-State bug fix) — the previous fast-path trusted
    // localStorage and skipped the BYEP/onboarding gates whenever the
    // browser had leftover caches from an incomplete signup.
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      setShowAI(false);
      return;
    }
    
    const API = process.env.REACT_APP_BACKEND_URL;
    setShowAI(true); // Optimistic — don't flash blank screen
    const _authToken =
      localStorage.getItem('access_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token');
    fetch(`${API}/api/auth/session-status/${userId}`, {
      headers: _authToken ? { Authorization: `Bearer ${_authToken}` } : {},
    })
      .then(res => res.json())
      .then(data => {
        // Cache backend state into localStorage (caches, not gates)
        if (data.terms_accepted) {
          localStorage.setItem('terms_accepted', 'true');
        } else {
          localStorage.removeItem('terms_accepted');
        }
        if (data.subscription_active) {
          localStorage.setItem('hasCompletedOnboarding', 'true');
        } else {
          localStorage.removeItem('hasCompletedOnboarding');
        }
        if (data.first_login_complete) {
          localStorage.removeItem('first_login_pending');
        }

        // Locked 2026-02 (PORTAL_ACTIVATION_DOCTRINE.md): cache the
        // activation state + walkthrough progress so the walkthrough
        // overlay can resume EXACTLY where the user left off across
        // refresh / session-expire / device-change. These are caches
        // only — backend session-status remains the source of truth.
        if (data.activation_state) {
          localStorage.setItem('activation_state', data.activation_state);
        }
        if (data.walkthrough_progress) {
          localStorage.setItem(
            'walkthrough_progress',
            JSON.stringify(data.walkthrough_progress),
          );
        }
        if (typeof data.walkthrough_version === 'number') {
          localStorage.setItem('walkthrough_version', String(data.walkthrough_version));
        }

        // Route based on the FIRST incomplete step. The signup_resume_step
        // is computed server-side and is the single source of truth.
        const resume = data.signup_resume_step;
        if (resume === 'terms-and-policy' && currentPath !== '/terms-and-policy') {
          setShowAI(false);
          navigate('/terms-and-policy');
          return;
        }
        if (resume === 'membership' && currentPath !== '/membership' && currentPath !== '/membership/success' && currentPath !== '/build-experience') {
          setShowAI(false);
          localStorage.setItem('signup_in_progress', '1');
          navigate('/membership');
          return;
        }
        if (resume === 'build-experience' && currentPath !== '/build-experience' && currentPath !== '/welcome-setup') {
          setShowAI(false);
          localStorage.setItem('signup_in_progress', '1');
          navigate('/build-experience');
          return;
        }
        // Locked 2026-06-01 — NC Gateway access.
        // Network Contractors get the compliance wizard instead of BYEP.
        if (resume === 'nc-onboarding' && !currentPath.startsWith('/nc-onboarding')) {
          setShowAI(false);
          localStorage.setItem('signup_in_progress', '1');
          navigate('/nc-onboarding');
          return;
        }
        // first-login-walkthrough resume routes to /dashboard normally
        // — the walkthrough overlays render there and clear themselves
        // via /api/auth/mark-first-login-complete.
        if (resume === 'first-login-walkthrough') {
          localStorage.setItem('first_login_pending', '1');
          localStorage.removeItem('signup_in_progress');
        } else if (!resume) {
          localStorage.removeItem('signup_in_progress');
        }
        setShowAI(true);
      })
      .catch(() => {
        // Network failure — allow access rather than blocking returning users
        setShowAI(true);
      });
  }, [location.pathname, navigate]);

  const handleExitOwnerImpersonation = () => {
    const backup = localStorage.getItem('owner_session_backup');
    if (backup) {
      try {
        const session = JSON.parse(backup);
        if (session.user) localStorage.setItem('analytics_user', session.user);
        if (session.token) localStorage.setItem('auth_token', session.token);
        const user = JSON.parse(session.user || '{}');
        if (user.id) localStorage.setItem('user_id', user.id);
        if (user.name) localStorage.setItem('user_name', user.name);
        if (user.role) localStorage.setItem('user_role', user.role);
        if (user.company_id) localStorage.setItem('company_id', user.company_id);
      } catch { /* handled */ }
    }
    localStorage.removeItem('owner_session_backup');
    localStorage.removeItem('owner_impersonating_worker');
    localStorage.removeItem('contractor_profile_id');
    navigate('/dashboard');
  };

  // Owner→worker banner only (the orange SA/company banner was REMOVED
  // entirely — founder directive 2026-09-27: Super Admin is a permissioned
  // section of the one Provider Portal, not an impersonation state).
  const isOwnerImpersonating = localStorage.getItem('owner_impersonating_worker') === 'true';
  const isPublicRoute = ['/', '/landing', '/login', '/analytics-login', '/signup', '/nc-signup', '/nc-landing', '/nc-login', '/forgot-password', '/reset-password', '/remodify', '/replay-demo', '/build-experience', '/membership', '/membership/success', '/welcome-setup', '/setup', '/terms-and-policy', '/super-admin/login', '/founders-checklist', '/privacy', '/terms', '/about', '/services', '/tools', '/tools/signup', '/join'].includes(location.pathname) || location.pathname.startsWith('/pro/') || location.pathname.startsWith('/customer-portal/') || location.pathname.startsWith('/remodify/') || location.pathname.startsWith('/reset-password/') || location.pathname.startsWith('/public/') || location.pathname.startsWith('/pay/') || location.pathname.startsWith('/payout-setup/') || location.pathname.startsWith('/track/') || location.pathname.startsWith('/portfolio/') || location.pathname.startsWith('/job/') || location.pathname.startsWith('/c/') || location.pathname === '/find' || location.pathname.startsWith('/find/');

  // View-mode toggle + Gateway home-button are mounted UNCONDITIONALLY in
  // the shell — locked 2026-05-31 per owner directive: "Put the RemodDash
  // toggle on the Gateway and every RemodDash-controlled page." Each
  // component owns its own self-suppression (DOM probe + pathname check)
  // so we don't need a per-route gate here.

  // Provider OS chrome scoping is now owned by ProviderSurfaceGate +
  // ProviderMobileApp / ProviderDesktopApp. App.js no longer needs to
  // compute isToolRoute / isSuperAdminRoute / isNCRoute / isProviderOSRoute
  // because the dual-surface architecture renders the right chrome
  // intrinsically for each route.

  // Track a true navigation STACK for the mobile footer Back button.
  // Push on forward navigation, pop on Back. A plain most-recent-unique list
  // re-orders on every navigation, which made Back ping-pong between the last
  // two pages instead of walking back. The Back handler sets
  // `nav_back_inprogress` so the page it lands on isn't immediately re-pushed.
  React.useEffect(() => {
    if (isPublicRoute) return;
    try {
      if (sessionStorage.getItem('nav_back_inprogress') === '1') {
        sessionStorage.removeItem('nav_back_inprogress');
        return;
      }
      const stack = JSON.parse(sessionStorage.getItem('nav_stack') || '[]');
      if (stack[stack.length - 1] !== location.pathname) {
        stack.push(location.pathname);
        sessionStorage.setItem('nav_stack', JSON.stringify(stack.slice(-20)));
      }
    } catch { /* ignore */ }
  }, [location.pathname, isPublicRoute]);

  // ─── Global route auth guard (security: prevents /dashboard,
  //     /analytics, /jobs etc. from rendering when no token is present).
  //     Runs on every route change and on storage changes so PWA
  //     standalone launches and post-login redirects Just Work. ───────
  React.useEffect(() => {
    if (isPublicRoute) return;
    // Super-admin routes authenticate via their own session_id flow
    if (location.pathname.startsWith('/super-admin/')) return;
    // Standalone tool routes have their own tools_only auth context —
    // never bounce them to the Provider /login. If their token actually
    // expired the in-tool API calls will fail and re-prompt locally.
    const isToolRoute =
      location.pathname === '/remodforms' || location.pathname.startsWith('/remodforms/') ||
      location.pathname === '/remodpay'   || location.pathname.startsWith('/remodpay/') ||
      location.pathname === '/remodinventory' || location.pathname.startsWith('/remodinventory/');

    const hasToken = !!(localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('access_token'));
    const hasAnalyticsUser = !!localStorage.getItem('analytics_user');
    const toolsOnly = localStorage.getItem('tools_only_mode') === '1';
    // Accept EITHER a JWT (main app) OR a legacy analytics_user blob
    if (!hasToken && !hasAnalyticsUser) {
      if (isToolRoute || toolsOnly) {
        navigate('/tools/signup', { replace: true });
      } else {
        navigate('/login', { replace: true, state: { from: location.pathname } });
      }
    }
  }, [location.pathname, isPublicRoute, navigate]);


  return (
    <>
      <ActivationGate />
      {appLocked && <AppLockScreen onUnlock={handleAppUnlock} />}
      {isOwnerImpersonating && !isPublicRoute && (
        <div 
          className="fixed top-0 left-0 right-0 z-[9999] bg-blue-600 text-white px-4 py-2 flex items-center justify-between shadow-lg"
          data-testid="owner-impersonation-banner"
        >
          <span className="text-sm font-medium">
            Viewing worker portal — {localStorage.getItem('user_name') || 'Worker'}
          </span>
          <button
            onClick={handleExitOwnerImpersonation}
            className="bg-white text-blue-700 px-3 py-1 rounded text-sm font-semibold hover:bg-blue-50 transition-colors"
            data-testid="exit-owner-impersonation-btn"
          >
            Back to Dashboard
          </button>
        </div>
      )}
      <div style={isOwnerImpersonating ? { paddingTop: '40px' } : {}}>
      <React.Suspense fallback={<div data-testid="route-suspense-fallback" style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#cbd5e1', fontSize: 14}}>Loading…</div>}>
      <Routes>
          <Route path="/" element={<Gateway />} />
          <Route path="/replay-demo" element={<ReplayDemo />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/build-experience" element={<SetupYourBusiness />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/membership/success" element={<MembershipSuccess />} />
          <Route path="/welcome-setup" element={<IndustryStarterOnboarding />} />
          <Route path="/setup" element={<SetupYourBusiness />} />
          <Route path="/terms-and-policy" element={<TermsAndPolicy />} />
          <Route path="/payout-setup/:companyId" element={<PayoutSetupStandalone />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/analytics-login" element={<AnalyticsLogin />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/nc-signup" element={NC_LAUNCH_LOCKED ? <Navigate to="/" replace /> : <NCSignUp />} />
          <Route path="/nc-landing" element={NC_LAUNCH_LOCKED ? <Navigate to="/" replace /> : <NCLandingPage />} />
          <Route path="/nc-login" element={NC_LAUNCH_LOCKED ? <Navigate to="/" replace /> : <NCLogin />} />
          <Route path="/settings/integrations/lead-sources" element={<IntegrationsLeadSources />} />
          <Route path="/login" element={<AnalyticsLogin />} />
          {/* V3 auth — per-company deep link: /c/RD-1234 pre-fills the company code */}
          <Route path="/c/:code" element={<AnalyticsLogin />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Public payment + sharing surfaces (no Provider OS chrome) */}
          <Route path="/pay/:token" element={<PaymentLinkPage />} />
          <Route path="/track/:token" element={<CustomerTracking />} />
          <Route path="/public/estimate/:token" element={<PublicEstimateView />} />
          <Route path="/public/portal-create" element={<PublicPortalCreate />} />
          <Route path="/public/sign/:token" element={<PublicSignForm />} />
          <Route path="/public/company/:companyId" element={<PublicCompanyProfile />} />
          <Route path="/portfolio/:companyId" element={<CompanyPortfolio />} />
          <Route path="/pro/:slug" element={<PublicProfile />} />
          <Route path="/find" element={<FindProvidersIndex />} />
          <Route path="/find/:slug" element={<FindProviders />} />
          <Route path="/job/:jobId/gallery" element={<JobGallery />} />

          {/* Customer Portal (Remodify) — separate surface, not Provider OS */}
          <Route path="/remodify" element={<PublicRemodify />} />
          <Route path="/remodify/review/:token" element={<QuickReview />} />
          <Route path="/remodify/:slug" element={<RemodifyPortal />} />

          {/* Tools-mode (separate portal) — public entry locked until launch */}
          <Route path="/tools" element={TOOLS_LAUNCH_LOCKED ? <Navigate to="/" replace /> : <Tools />} />
          <Route path="/tools/signup" element={TOOLS_LAUNCH_LOCKED ? <Navigate to="/" replace /> : <ToolsSignup />} />
          <Route path="/remodforms" element={<RemodForms standaloneMode={true} />} />
          <Route path="/remodpay" element={<RemodPay standaloneMode={true} />} />
          <Route path="/remodinventory" element={<SmartInventoryUnified standaloneMode={true} />} />

          {/* Super Admin Portal — separate surface, not Provider OS */}
          <Route path="/super-admin/login" element={<SuperAdminLogin />} />
          <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
          <Route path="/super-admin/ari-diagnostics" element={<AriDiagnosticsDashboard />} />
          <Route path="/super-admin/ari-decisions" element={<AriDecisionJournal />} />
          <Route path="/super-admin/controls" element={<SuperAdminControls />} />
          <Route path="/super-admin/operational-simulator" element={<SuperAdminOperationalSimulator />} />
          <Route path="/super-admin/operational-intelligence" element={<SuperAdminOperationalIntelligence />} />
          <Route path="/super-admin/manual-lead-routing" element={<SuperAdminManualLeadRouting />} />
          <Route path="/super-admin/workspace-reviews" element={<SuperAdminWorkspaceReviews />} />
          <Route path="/super-admin/activation-funnel" element={<SuperAdminActivationFunnel />} />
          <Route path="/super-admin/founder-program" element={<SuperAdminFounderProgram />} />
          <Route path="/super-admin/growth" element={<SuperAdminGrowth />} />
          <Route path="/super-admin/security" element={<SecurityCenter />} />
          <Route path="/super-admin/phone" element={<SuperAdminPhone />} />
          <Route path="/super-admin/customer-acquisition" element={<SuperAdminCustomerAcquisition />} />
          <Route path="/join" element={<Join />} />
          <Route path="/super-admin/search-intelligence" element={<SuperAdminSearchIntelligence />} />
          <Route path="/discoverability" element={<ProviderDiscoverability />} />
          <Route path="/super-admin/platform-rules" element={<PlatformRulesAdmin />} />
          <Route path="/super-admin/nc-management" element={<SuperAdminNCManagement />} />
          <Route path="/super-admin/feedback" element={<SuperAdminFeedback />} />

          {/* Demo Mode remount-bounce target — DemoMode navigates here for
              ~90ms between steps to force the real destination page to remount
              and refetch. Lightweight so the full Provider surface never spins
              up for the bounce. Locked 2026-06-10. */}
          <Route path="/__demo" element={<div data-testid="demo-bounce" style={{ minHeight: '100vh', background: '#0a1230' }} />} />

          {/* ═══ Provider Operating System — Dual-Surface Architecture ═══
              Locked 2026-05-30. The ProviderSurfaceGate XOR-mounts EXACTLY
              ONE of <ProviderMobileApp /> or <ProviderDesktopApp /> based on
              ViewMode + device class.
              Neither surface knows the other exists. All Provider OS routes
              (/dashboard, /jobs, /invoices, /settings/*, /nc-portal, etc.)
              live INSIDE the chosen surface's own <Routes> block. Switching
              surfaces unmounts the entire tree and mounts the other fresh —
              state cannot bleed because nothing is shared below this gate.
              The only shared layer is the service layer (auth, API, Ari
              Command Center drawer, backend). */}
          <Route path="/*" element={<ProviderSurfaceGate ariMode={ariAccess.mode} />} />
        </Routes>
      </React.Suspense>
        
        {/* PWA Install Prompt (mobile only) */}
        {showAI && <PWAInstallPrompt />}

        {/* Public landing/gateway install badge — desktop + mobile, only
            on public routes, auto-hides when browser can't install or the
            PWA is already installed. Never shifts page layout (fixed pos). */}
        {isPublicRoute && <PublicInstallBadge />}
        
{/* GPS Mileage Tracker removed - contractors access via their portal */}

        {/* ══ Provider-OS chrome (MobileFooterNav, AriInsightsOverlay bottom-glow,
            RemodConnectLeadPopup, OnboardingOverlay) is OWNED by the Provider
            surfaces themselves (ProviderMobileApp / ProviderDesktopApp). Locked
            2026-05-30 — Dual-Surface Architecture. App.js no longer mounts any
            of these globally. Switching surfaces tears down the entire chrome
            tree along with the surface, so mobile chrome physically cannot
            render in desktop mode and vice versa. ══ */}

        {/* ══ Ari Command Center drawer — global listener for `ari:open-command-center`
            window event dispatched by <PortalBriefing /> clicks. Works in EVERY
            portal (including Customer + Tools where the bottom-glow overlay is
            hidden). Phase A Hardening — Operational Audit (May 13, 2026). ══ */}
        <AriCommandCenterDrawer />

        {/* ══ Platform Communications (Doctrine D) — global MANDATORY popup.
            Polls /api/platform-comms/pending for the signed-in provider and
            shows unacked mandatory announcements as a full-screen blocking
            popup (cannot be overlooked). ══ */}
        <MandatoryCommPopup />


        {/* ══ Reward popup scaffold (Feb 2026 Controlled Correction).
            Dormant — listens for window 'remod:reward' CustomEvents. No
            triggers wired yet; future pass decides where to fire events. ══ */}
        {!isPublicRoute && <RewardPopup />}

        {/* ══ Global Portal-Shell View-Mode Toggle — locked 2026-05-31 per
            owner directive: "Put the RemodDash Mobile/Desktop toggle on the
            Gateway and every RemodDash-controlled page, including auth
            pages, so users never need Chrome Desktop Site."

            Mounts the compact ViewModeToggle in a fixed top-right slot on
            EVERY route, plus an Info popover that surfaces the doctrine
            ("Use the RemodDash view toggle instead of browser Desktop Site").

            Includes browser "Desktop Site" override detection (one-time
            toast) + viewport-meta MutationObserver guard so external
            browser modes cannot corrupt our layout.

            De-duplication: auto-hides via DOM probe when ANY existing
            inline `view-mode-toggle` is on the page (ProviderDesktopChrome,
            RemodDashboard inline, SuperAdminDashboard header inline). ══ */}
        <GlobalPortalShellToggle />

        {/* ══ Gateway Home Button — fixed top-left "← Gateway" pill on every
            non-Gateway page (auto-hides on `/`, `/super-admin/login`,
            customer/public-link surfaces, and when ProviderDesktopChrome's
            back-button is on the page).

            Locked 2026-05-31 — "Ensure every page has a route back to
            Gateway." ══ */}
        <GatewayHomeButton />

        {/* ══ Super Admin Bottom Nav — fixed mobile-only bottom nav inside
            `/super-admin/*` (Home → Gateway, Dashboard → SA dashboard).
            Replaces reliance on the Android system back button inside
            the SA portal.

            Locked 2026-06-03 — owner directive: "Add a bottom navigation
            bar to the Super Admin Portal mobile version." ══ */}
        <SuperAdminBottomNav />

        {/* ══ Demo Mode director (Provider) — mounted globally inside the
            Router. Activates on the `demo:start` event (fired by
            launchProviderDemo from the Gateway) or when localStorage
            `demo_mode === 'provider'`. Auto-logs the visitor into the REAL
            Provider portal as the isolated demo tenant and drives the real
            app. Renders the deep-blue Ari director dock + persistent
            "DEMO MODE — sample data · no charges" banner. Locked 2026-06-10. ══ */}
        <DemoMode />
        <CustomerDemoMode />
      </div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <ViewModeProvider>
          <TradeProvider>
            <TutorialProvider>
              <MobileGuidanceProvider>
                <ThemeProvider>
                  <BrowserRouter>
                    <ScrollToTop />
                    <ActivationProvider>
                      <BusinessSetupProvider>
                        <TerminologyProvider>
                          <AppContent />
                        </TerminologyProvider>
                      </BusinessSetupProvider>
                    </ActivationProvider>
                    <FeedbackWidget />
                    <FloatingSoftphone />
                  </BrowserRouter>
                  <Toaster />
                </ThemeProvider>
              </MobileGuidanceProvider>
            </TutorialProvider>
          </TradeProvider>
        </ViewModeProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;